'use client'

import { useTranslation } from '@shared/config/i18n'
import clsx from 'clsx'
import { useRouter } from 'next/navigation'
import { overlay } from 'overlay-kit'
import React, { ComponentProps } from 'react'
import { match } from 'ts-pattern'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogV1,
  AlertDialogV1Cancel,
  AlertDialogV1Content,
  AlertDialogV1Description,
  AlertDialogV1Footer,
  AlertDialogV1Header,
  AlertDialogV1Title,
  Button,
  HStack,
  VStack,
} from '@podo-app/design-system-temp'
import { CloseIcon } from '@podo/assets'

type Props = {
  close: (prams?: unknown) => void
  header: (props: { handleClose: () => void }) => React.JSX.Element
  footer: (props: { handleClose: () => void }) => React.JSX.Element
} & ComponentProps<typeof AlertDialogV1>

export const AlertDialogContainer = ({ open, close, header: HeaderContent, footer: FooterContent }: Props) => {
  return (
    <AlertDialogV1 open={open} onOpenChange={(open) => !open && close()}>
      <AlertDialogV1Content className="mobile:min-w-[335px] tablet:max-w-[420px]">
        <AlertDialogV1Header>
          <HeaderContent handleClose={close} />
        </AlertDialogV1Header>
        <AlertDialogV1Footer>
          <FooterContent handleClose={close} />
        </AlertDialogV1Footer>
      </AlertDialogV1Content>
    </AlertDialogV1>
  )
}

const DefaultAlertConfirmButton = ({ close }: { close: () => void }) => {
  const t = useTranslation()

  return (
    <Button
      size="xLarge"
      fullWidth
      onClick={async () => {
        close()
      }}
    >
      {t('common.actions.confirm')}
    </Button>
  )
}

export const openAlertDialog = (
  options:
    | {
        title: React.ReactNode
        description?: React.ReactNode
        image?: React.ReactNode
        closable?: boolean
        confirmMessage: React.ReactNode
        handleConfirm?: () => void | Promise<void>
      }
    | {
        title: React.ReactNode
        description?: React.ReactNode
        image?: React.ReactNode
        closable?: boolean
        confirmMessage: React.ReactNode
        redirectPath: string
        router: { push: (path: string) => void }
      }
    | {
        title: React.ReactNode
        description?: React.ReactNode
        image?: React.ReactNode
        closable?: boolean
      },
) => {
  const { title, description, image, closable = false, ...restOptions } = options
  return overlay.open(({ isOpen, close }) => (
    <AlertDialogV1 open={isOpen} onOpenChange={(open) => !open && close()}>
      <AlertDialogV1Content className="mobile:min-w-[335px] tablet:max-w-[420px]" onOverlayClick={() => close()}>
        <HStack className="relative w-full justify-center items-center">
          {closable && (
            <AlertDialogV1Cancel className="absolute top-0 right-0">
              <CloseIcon />
            </AlertDialogV1Cancel>
          )}

          {image}
        </HStack>
        <AlertDialogV1Header>
          <AlertDialogV1Title className="text-center">{title}</AlertDialogV1Title>
          {description ? (
            <AlertDialogV1Description className="text-center whitespace-pre-line">
              {description}
            </AlertDialogV1Description>
          ) : null}
        </AlertDialogV1Header>
        <AlertDialogV1Footer>
          {'confirmMessage' in restOptions && restOptions.confirmMessage ? (
            <Button
              size="xLarge"
              fullWidth
              onClick={async () => {
                close()
                if ('handleConfirm' in restOptions) {
                  await restOptions.handleConfirm?.()
                } else if ('redirectPath' in restOptions) {
                  const { redirectPath, router } = restOptions
                  router.push(redirectPath)
                }
              }}
            >
              {restOptions.confirmMessage}
            </Button>
          ) : (
            <DefaultAlertConfirmButton close={close} />
          )}
        </AlertDialogV1Footer>
      </AlertDialogV1Content>
    </AlertDialogV1>
  ))
}

type AlertDialogContainerV2Props = {
  close: (params?: unknown) => void
  AlertHeader: (props: { handleClose: () => void }) => React.JSX.Element
  AlertFooter: (props: { handleClose: () => void; push: (path: string) => void }) => React.JSX.Element
  direction?: 'horizontal' | 'vertical'
  overlayId?: string
  className?: string
} & ComponentProps<typeof AlertDialog>

export const AlertDialogContainerV2 = ({
  open,
  close,
  AlertHeader,
  AlertFooter,
  direction = 'horizontal',
  className,
}: AlertDialogContainerV2Props) => {
  const router = useRouter()
  return (
    <AlertDialog open={open} onOpenChange={(open) => !open && close()}>
      <AlertDialogContent className={clsx('mobile:min-w-[335px] tablet:max-w-[420px]', className)}>
        <AlertDialogHeader className="relative">
          <AlertHeader handleClose={close} />
        </AlertDialogHeader>
        <AlertDialogFooter>
          {match(direction)
            .with('horizontal', () => (
              <HStack as={AlertDialogFooter} className="gap-2">
                <AlertFooter handleClose={close} push={(path) => router.push(path)} />
              </HStack>
            ))
            .with('vertical', () => (
              <VStack as={AlertDialogFooter} className="gap-2">
                <AlertFooter handleClose={close} push={(path) => router.push(path)} />
              </VStack>
            ))
            .exhaustive()}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const openAsyncAlertDialog = async (props: Omit<Props, 'open' | 'close'>) => {
  return await overlay.openAsync(({ isOpen, close }) => <AlertDialogContainer open={isOpen} close={close} {...props} />)
}

export const openAlertDialogV2 = (
  props: Omit<AlertDialogContainerV2Props, 'open' | 'close'> & { onClose?: () => void },
) => {
  const { onClose, ...dialogProps } = props
  return overlay.open(
    ({ isOpen, close }) => (
      <AlertDialogContainerV2
        open={isOpen}
        close={() => {
          close()
          onClose?.()
        }}
        {...dialogProps}
      />
    ),
    { overlayId: props.overlayId },
  )
}
